'use client'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useRef, useState, useEffect } from 'react'
import { createMarket } from '@/app/actions/markets'
import { createEvent } from '@/app/actions/events'
import { fetchUsers } from '@/app/actions/users'
import type { UserBasic } from '@/app/actions/users'
import CollapsibleSection from './CollapsibleSection'
import SeedBadge from './SeedBadge'
import { toEasternISO, todayET } from '@/lib/time'


function nowEST(): string {
  return new Date().toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const LP_SEED = 1000  // internal units — mirrors backend LP_SEED
const HIGH_ROLLER_SEED = 10000  // internal units — mirrors backend HIGH_ROLLER_SEED

// Event outcome seed options (internal units) — mirrors backend CreateEventBody.seed
const EVENT_SEED_OPTIONS: { value: 1000 | 2500 | 5000 | 10000; label: string }[] = [
  { value: 1000, label: '100K' },
  { value: 2500, label: '250K' },
  { value: 5000, label: '500K' },
  { value: 10000, label: '1M' },
]

export default function CreateMarketButton({
  isLoggedIn,
  loginUrl,
  balance,
  categories,
}: {
  isLoggedIn: boolean
  loginUrl: string
  balance: string | null
  categories: string[]
}) {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [type, setType] = useState<'binary' | 'event'>('binary')
  const [tier, setTier] = useState<'standard' | 'high_roller'>('standard')
  const [eventSeed, setEventSeed] = useState<1000 | 2500 | 5000 | 10000>(1000)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [selectedCats, setSelectedCats] = useState<string[]>([])
  const [closeDate, setCloseDate] = useState('')
  const [closeTime, setCloseTime] = useState('00:00')
  const [currentET, setCurrentET] = useState('')
  const [labels, setLabels] = useState<string[]>(['', ''])
  const [yesLabel, setYesLabel] = useState('')
  const [noLabel, setNoLabel] = useState('')
  const [restrictedUsers, setRestrictedUsers] = useState<UserBasic[]>([])
  const [restrictionMode, setRestrictionMode] = useState<'hide' | 'inside_joke'>('hide')
  const [interactionLevel, setInteractionLevel] = useState<'isolated' | 'neutral' | 'interactive'>('neutral')
  const [userSearch, setUserSearch] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [allUsers, setAllUsers] = useState<UserBasic[]>([])
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingEvent, setPendingEvent] = useState<{
    question: string
    description?: string
    closes_at?: string
    labels: string[]
    seed: 1000 | 2500 | 5000 | 10000
  } | null>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const confirmBackdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && !usersLoaded) {
      fetchUsers().then(u => { setAllUsers(u); setUsersLoaded(true) })
    }
  }, [open, usersLoaded])

  function toggleCat(cat: string) {
    setSelectedCats(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    )
  }

  function resetForm() {
    setType('binary')
    setTier('standard')
    setEventSeed(1000)
    setSelectedCats([])
    setCloseDate('')
    setCloseTime('00:00')
    setError('')
    setLabels(['', ''])
    setYesLabel('')
    setNoLabel('')
    setRestrictedUsers([])
    setRestrictionMode('hide')
    setInteractionLevel('neutral')
    setUserSearch('')
    setConfirmOpen(false)
    setPendingEvent(null)
  }

  function handleOpen() {
    setOpen(true)
    setError('')
    setCurrentET(nowEST())
  }

  if (!isLoggedIn) {
    return (
      <a
        href={loginUrl}
        title="Create a Market"
        className="shrink-0 rounded-full bg-indigo-600 p-1.5 sm:p-2 text-white hover:bg-indigo-500 transition-colors flex items-center gap-2 lg:px-4 lg:py-2"
      >
        <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
        </svg>
        <span className="hidden lg:inline text-sm font-semibold whitespace-nowrap">Create a Market</span>
      </a>
    )
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError('')

    const fd = new FormData(e.currentTarget)
    const closes_at = closeDate ? toEasternISO(closeDate, closeTime) : undefined

    if (closes_at && new Date(closes_at) <= new Date()) {
      setError('Close date must be in the future')
      return
    }

    if (type === 'binary') {
      if ((yesLabel.trim() !== '') !== (noLabel.trim() !== '')) {
        setError('Both YES and NO labels must be filled in, or both left empty')
        return
      }
      setSubmitting(true)
      const result = await createMarket({
        question: fd.get('question') as string,
        description: (fd.get('description') as string) || undefined,
        categories: selectedCats,
        closes_at,
        restricted_user_ids: restrictedUsers.map(u => u.id),
        is_inside_joke: restrictionMode === 'inside_joke',
        interaction_allowance: interactionLevel,
        yes_label: yesLabel.trim() || undefined,
        no_label: noLabel.trim() || undefined,
        tier,
      })
      setSubmitting(false)
      if (result.error) {
        setError(result.error)
      } else {
        setOpen(false)
        resetForm()
        router.push(`/markets/${result.id}`)
      }
    } else {
      const cleanLabels = labels.map(l => l.trim()).filter(Boolean)
      if (cleanLabels.length < 2) {
        setError('At least 2 outcome labels are required')
        return
      }
      setPendingEvent({
        question: fd.get('question') as string,
        description: (fd.get('description') as string) || undefined,
        closes_at,
        labels: cleanLabels,
        seed: eventSeed,
      })
      setConfirmOpen(true)
    }
  }

  async function handleConfirmCreateEvent() {
    if (!pendingEvent) return
    setSubmitting(true)
    const result = await createEvent({
      question: pendingEvent.question,
      description: pendingEvent.description,
      categories: selectedCats,
      closes_at: pendingEvent.closes_at,
      labels: pendingEvent.labels,
      restricted_user_ids: restrictedUsers.map(u => u.id),
      is_inside_joke: restrictionMode === 'inside_joke',
      interaction_allowance: interactionLevel,
      seed: pendingEvent.seed,
    })
    setSubmitting(false)
    if (result.error) {
      setConfirmOpen(false)
      setError(result.error)
    } else {
      setConfirmOpen(false)
      setPendingEvent(null)
      setOpen(false)
      resetForm()
      router.push(`/events/${result.id}`)
    }
  }

  const outcomeCount = type === 'binary' ? 1 : labels.filter(l => l.trim()).length
  const seedPerOutcome = type === 'binary'
    ? (tier === 'high_roller' ? HIGH_ROLLER_SEED : LP_SEED)
    : eventSeed
  const lpCostInternal = seedPerOutcome * outcomeCount
  const lpCost = `🧀${(lpCostInternal * 100).toLocaleString('en-US')}`
  const canAfford = balance === null || parseFloat(balance) >= lpCostInternal
  const canClaimUnemployment = balance !== null && parseFloat(balance) < LP_SEED

  return (
    <>
      <button
        onClick={handleOpen}
        title="Create a Market"
        className="shrink-0 rounded-full bg-indigo-600 p-1.5 sm:p-2 text-white hover:bg-indigo-500 transition-colors flex items-center gap-2 lg:px-4 lg:py-2"
      >
        <svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
        </svg>
        <span className="hidden lg:inline text-sm font-semibold whitespace-nowrap">Create a Market</span>
      </button>

      {open && (
        <div
          ref={backdropRef}
          onClick={(e) => { if (e.target === backdropRef.current) setOpen(false) }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 flex flex-col max-h-[90dvh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Create a Market</h2>
              <button
                onClick={() => { setOpen(false); resetForm() }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            {/* Scrollable form body */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-5 overflow-y-auto">

              {/* Type toggle */}
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
                <button
                  type="button"
                  onClick={() => { setType('binary'); setError('') }}
                  className={`rounded-lg py-1.5 text-sm font-semibold transition-all ${type === 'binary' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  Binary Market
                </button>
                <button
                  type="button"
                  onClick={() => { setType('event'); setError('') }}
                  className={`rounded-lg py-1.5 text-sm font-semibold transition-all ${type === 'event' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  Multi-Outcome Event
                </button>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Question <span className="text-red-500">*</span>
                </label>
                <input
                  name="question"
                  type="text"
                  required
                  placeholder={type === 'binary' ? 'Will... ?' : 'Who will... ?'}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Outcomes (event only) */}
              {type === 'event' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Outcomes <span className="text-red-500">*</span>
                    <span className="ml-1.5 text-xs font-normal text-gray-400">(2–16)</span>
                  </label>
                  <div className="flex flex-col gap-2">
                    {labels.map((lbl, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="text"
                          value={lbl}
                          onChange={e => {
                            const next = [...labels]
                            next[idx] = e.target.value
                            setLabels(next)
                          }}
                          placeholder={`Outcome ${idx + 1}`}
                          className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {labels.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setLabels(labels.filter((_, i) => i !== idx))}
                            className="rounded-lg px-2 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                    {labels.length < 16 && (
                      <button
                        type="button"
                        onClick={() => setLabels([...labels, ''])}
                        className="self-start rounded-lg px-3 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950 transition-colors"
                      >
                        + Add outcome
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Categories
                  {selectedCats.length > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-indigo-500">({selectedCats.length} selected)</span>
                  )}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCat(cat)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        selectedCats.includes(cat)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Close Date <span className="text-xs font-normal text-gray-400">(EST)</span>
                </label>
                <div className="flex flex-row flex-wrap gap-2">
                  <input
                    type="date"
                    value={closeDate}
                    min={todayET()}
                    onChange={e => setCloseDate(e.target.value)}
                    className="flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="time"
                    value={closeTime}
                    onChange={e => setCloseTime(e.target.value)}
                    disabled={!closeDate}
                    className="w-32 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                  />
                </div>
                {currentET && (
                  <p className="text-xs text-gray-400">Current time in EST: {currentET}</p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Rules / Description</label>
                <textarea
                  name="description"
                  rows={2}
                  maxLength={500}
                  placeholder="How will this market resolve? (optional)"
                  className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>

              {/* Unlisted toggle — disabled */}
              {/* <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input type="checkbox" className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Unlisted — only accessible via direct link</span>
              </label> */}
              {/* Secret market section */}
              <CollapsibleSection title="Secret Market" subtitle="Hide this market from certain users?">

                {/* Mode toggle */}
                <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 text-xs font-medium">
                  <button
                    type="button"
                    onClick={() => setRestrictionMode('hide')}
                    className={`flex-1 px-3 py-2 transition-colors ${restrictionMode === 'hide' ? 'bg-indigo-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    Restricted Users
                  </button>
                  <button
                    type="button"
                    onClick={() => setRestrictionMode('inside_joke')}
                    className={`flex-1 px-3 py-2 border-l border-gray-200 dark:border-gray-700 transition-colors ${restrictionMode === 'inside_joke' ? 'bg-indigo-600 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    Inside Joke
                  </button>
                </div>

                <p className="text-xs text-gray-400">
                  {restrictionMode === 'hide'
                    ? 'These users CANNOT see the market until it resolves. Its against site rules to tell a restricted user about the market. If a restricted user finds out about the market, the market will be cancelled and all shares will be refunded'
                    : 'ONLY these users CAN see the market before it resolves.'}
                </p>

                {/* Selected chips */}
                {restrictedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {restrictedUsers.map(u => (
                      <span key={u.id} className={`inline-flex items-center gap-1 rounded-full pl-1 pr-2 py-0.5 text-xs font-medium ${
                        restrictionMode === 'hide'
                          ? 'bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                          : 'bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                      }`}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={u.avatar_url} alt="" className="h-4 w-4 rounded-full" />
                        {u.username}
                        <button type="button" onClick={() => setRestrictedUsers(prev => prev.filter(x => x.id !== u.id))} className={`ml-0.5 ${restrictionMode === 'hide' ? 'text-red-400 hover:text-red-600 dark:hover:text-red-200' : 'text-green-400 hover:text-green-600 dark:hover:text-green-200'}`}>✕</button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search input */}
                <div className="relative">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                    onFocus={() => setUserDropdownOpen(true)}
                    onBlur={() => setTimeout(() => setUserDropdownOpen(false), 150)}
                    placeholder="Search users…"
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {userDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg z-10 overflow-hidden max-h-48 overflow-y-auto">
                      {allUsers
                        .filter(u => !restrictedUsers.some(r => r.id === u.id) && u.username.toLowerCase().includes(userSearch.toLowerCase()))
                        .map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => { setRestrictedUsers(prev => [...prev, u]); setUserSearch('') }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img src={u.avatar_url} alt="" className="h-6 w-6 rounded-full shrink-0" />
                            <span className="text-gray-800 dark:text-gray-200">{u.username}</span>
                          </button>
                        ))}
                      {allUsers.filter(u => !restrictedUsers.some(r => r.id === u.id) && u.username.toLowerCase().includes(userSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-2 text-sm text-gray-400">No users found</p>
                      )}
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              {/* Custom YES/NO labels (binary only) */}
              {type === 'binary' && (
                <CollapsibleSection title="Custom YES/NO Labels" subtitle="Add custom text to display instead of YES and NO" defaultOpen={yesLabel !== '' || noLabel !== ''}>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      maxLength={12}
                      value={yesLabel}
                      onChange={e => { setYesLabel(e.target.value); setError('') }}
                      placeholder="YES label"
                      className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <input
                      type="text"
                      maxLength={12}
                      value={noLabel}
                      onChange={e => { setNoLabel(e.target.value); setError('') }}
                      placeholder="NO label"
                      className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                  <p className="text-xs text-gray-400">e.g. &quot;John&quot; / &quot;Bob&quot; — replaces YES/NO on betting buttons. Both or neither.</p>
                </CollapsibleSection>
              )}
              {/* Liquidity tier toggle (binary markets only) */}
              {type === 'binary' && (
                <div className="grid grid-cols-2 gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
                  <button
                    type="button"
                    onClick={() => setTier('standard')}
                    className={`rounded-lg py-1.5 text-sm font-semibold transition-all ${tier === 'standard' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    Standard Market (🧀100K)
                  </button>
                  <button
                    type="button"
                    onClick={() => setTier('high_roller')}
                    className={`rounded-lg py-1.5 text-sm font-semibold transition-all ${tier === 'high_roller' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                  >
                    High-Rollers Market (🧀1M)
                  </button>
                </div>
              )}

              {/* Initial seed selector (events only — same seed applies to every outcome) */}
              {type === 'event' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Initial Seed (per outcome)</label>
                  <div className="grid grid-cols-4 gap-1 rounded-xl bg-gray-100 dark:bg-gray-800 p-1">
                    {EVENT_SEED_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEventSeed(opt.value)}
                        className={`rounded-lg py-1.5 text-sm font-semibold transition-all ${eventSeed === opt.value ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Liquidity notice */}
              <div className={`rounded-xl border p-4 ${canAfford ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30' : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30'}`}>
                <p className={`text-xs leading-relaxed ${canAfford ? 'text-indigo-600 dark:text-indigo-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {type === 'binary'
                    ? "This seeds the initial liquidity pool at 50/50 odds. When the market resolves, you'll receive the pool's winning-side reserve as your LP payout."
                    : `Each outcome is an independent binary market seeded at 50/50. Resolve each one independently.`}
                </p>
                {!canAfford && (
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mt-2">
                    You don&apos;t have enough cheddar to create this market.
                  </p>
                )}
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}

              <div className="flex justify-end gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => { setOpen(false); resetForm() }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                {!canAfford ? (
                  <Link
                    href={canClaimUnemployment ? '/unemployment' : '/casino'}
                    onClick={() => { setOpen(false); resetForm() }}
                    className="rounded-lg bg-amber-500 hover:bg-amber-400 px-4 py-2 text-sm font-semibold text-white transition-colors"
                  >
                    {canClaimUnemployment ? 'Beg for Mercy' : 'Hit the Casino 🎰'}
                  </Link>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors"
                  >
                    {submitting ? 'Creating…' : `Create ${type === 'binary' ? 'Market' : 'Event'} (${lpCost})`}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmOpen && pendingEvent && (
        <div
          ref={confirmBackdropRef}
          onClick={(e) => { if (e.target === confirmBackdropRef.current) setConfirmOpen(false) }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 flex flex-col p-6 gap-4">
            <p className="text-sm text-gray-800 dark:text-gray-100">
              You are creating Event: <span className="font-semibold">{pendingEvent.question}</span> with markets
            </p>
            <ul className="list-disc pl-5 flex flex-col gap-1">
              {pendingEvent.labels.map((lbl, idx) => (
                <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">{lbl}</li>
              ))}
            </ul>
            <div className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100">
              <span>Initial Seed (per outcome):</span>
              <SeedBadge seed={pendingEvent.seed} />
            </div>
            <p className="text-xs text-gray-400">
              Total cost: 🧀{(pendingEvent.seed * pendingEvent.labels.length * 100).toLocaleString('en-US')}{' '}
              ({pendingEvent.labels.length} outcomes × 🧀{(pendingEvent.seed * 100).toLocaleString('en-US')})
            </p>
            <p className="text-sm text-gray-800 dark:text-gray-100">
              Are you sure you would like to proceed?
            </p>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                className="rounded-lg bg-red-600 hover:bg-red-500 px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateEvent}
                disabled={submitting}
                className="rounded-lg bg-green-600 hover:bg-green-500 px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              >
                {submitting ? 'Creating…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
